import { Box, Checkbox, FormControlLabel } from '@mui/material';
import { Controller, Path, PathValue } from 'react-hook-form';
import { BaseControlledInputProps } from '../BasicControlledInputProps';
import FormFieldWrappers from '../FormFieldWrappers';
import { ReactNode } from 'react';
import { ExpandableGroup } from '../ExpandableGroup/ExpandableGroupComponent';
import {
  useFormTracking,
  useFormOptionTracking,
} from '../hooks/useFormTracking';

export type CheckboxOption<VALUE> = {
  label: ReactNode;
  value: VALUE;
  disabled?: boolean;
};

type CheckboxGroupProps<VALUE> = {
  options: CheckboxOption<VALUE>[];
  accordionTitle?: string;
  defaultExpanded?: boolean;
};

// Type helper to extract element type from array
type ArrayElement<T> = T extends (infer U)[] ? U : never;

export type ControlledCheckboxGroupProps<
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>
> = Omit<
  BaseControlledInputProps<FORM_TYPE, FIELD_NAME>,
  'onBlurCallback' | 'onFocusCallback' | 'ariaLabel' | 'ariaRole'
> &
  CheckboxGroupProps<ArrayElement<PathValue<FORM_TYPE, FIELD_NAME>>>;

export const ControlledCheckboxGroup = <
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>
>({
  name,
  label,
  defaultValue = [] as PathValue<FORM_TYPE, FIELD_NAME>,
  disabled,
  hidden,
  control,
  required,
  options,
  trackingName,
  sensitive = true,
}: ControlledCheckboxGroupProps<FORM_TYPE, FIELD_NAME>) => {
  // Auto-generate tracking props if trackingName is provided
  const trackingProps = useFormTracking(trackingName);
  const { generateOptionTracking } = useFormOptionTracking(
    trackingName,
    sensitive,
    options
  );

  return (
    <FormFieldWrappers.FieldWrapper>
      <FormFieldWrappers.InputWrapper>
        <Controller
          control={control}
          name={name}
          rules={{ required: required ? `${label} is required` : false }}
          defaultValue={defaultValue}
          render={({ field: { onChange, value } }) => {
            const currentValue = (value || []) as ArrayElement<
              PathValue<FORM_TYPE, FIELD_NAME>
            >[];

            const handleChange = (
              optionValue: ArrayElement<PathValue<FORM_TYPE, FIELD_NAME>>
            ) => {
              const newValue = currentValue.includes(optionValue)
                ? currentValue.filter(v => v !== optionValue)
                : [...currentValue, optionValue];
              onChange(newValue);
            };

            return (
              !hidden && (
                <ExpandableGroup title={label} {...trackingProps}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {options.map(option => (
                      <FormControlLabel
                        key={String(option.value)}
                        control={
                          <Checkbox
                            {...generateOptionTracking(
                              option.value,
                              option.label as unknown as string
                            )}
                            checked={currentValue.includes(option.value)}
                            onChange={() => handleChange(option.value)}
                            disabled={disabled || option.disabled}
                          />
                        }
                        label={option.label}
                      />
                    ))}
                  </Box>
                </ExpandableGroup>
              )
            );
          }}
        />
      </FormFieldWrappers.InputWrapper>
    </FormFieldWrappers.FieldWrapper>
  );
};
